import { useCallback, useEffect, useMemo, useState } from 'react';
import { commandCenterApi, formatCurrency } from '../../utils/api';
import Modal from '../../components/Modal';
import StatInfo from '../../components/StatInfo';
import { LoadState, formatDate } from './parts';
import { Dash, fmt } from './statsParts';

// One supplier across every restaurant that works with it: what each one has
// configured, and what each one pays per product. The two things a future
// platform supplier entity would own a DEFAULT of, with a per-restaurant
// override — so a column that disagrees between restaurants is marked: it is
// exactly where an override would be needed (lib/ccSupplierStats.js).

// ─── Settings per restaurant ─────────────────────────────────────────────────

function SettingValue({ field, value }) {
  if (field.kind === 'flag') return value ? <span className="cc-pill ok">כן</span> : <Dash />;
  if (!value) return <span className="cc-muted">לא הוגדר</span>;
  if (field.key !== 'invoice_rules') return value;
  // Free text, up to 4 KB: "יש" in the cell, the text itself on tap.
  return (
    <span className="cc-types">
      יש
      <StatInfo label={field.label} ariaLabel={`${field.label}: הצגת הטקסט`} maxWidth={320}>
        <div style={{ whiteSpace: 'pre-wrap' }}>{value}</div>
      </StatInfo>
    </span>
  );
}

function SettingsTable({ rows, settings, differs }) {
  const differ = new Set(differs);
  return (
    <div className="cc-matrix-scroll">
      <table className="cc-matrix cc-supplier-settings">
        <thead>
          <tr>
            <th className="cc-matrix-name">מסעדה</th>
            <th>שם אצל המסעדה</th>
            <th title="ח.פ. הספק — שהוזן ידנית, או כפי שמודפס על המסמכים שלו במסעדה זו">ח.פ.</th>
            <th>מסמכים</th>
            <th>פעילות אחרונה</th>
            <th>מוצרים בקטלוג</th>
            <th>מחירים מוסכמים</th>
            {settings.map(f => (
              <th key={f.key} className={differ.has(f.key) ? 'is-diff' : undefined}
                title={differ.has(f.key) ? 'שונה בין המסעדות' : undefined}>
                {f.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.supplier_id}>
              <td className="cc-matrix-name">{r.restaurant_name}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{r.name}</td>
              <td className="cc-num">
                {r.entered_business_id
                  ? <>{r.entered_business_id} <span className="cc-price-tag">הוזן</span></>
                  : r.printed_business_id
                    ? <>{r.printed_business_id} <span className="cc-price-tag">ממסמכים</span></>
                    : <Dash />}
              </td>
              <td className="cc-num">{r.docs ? fmt(r.docs) : <Dash />}</td>
              <td className="cc-num">{formatDate(r.last_doc)}</td>
              <td className="cc-num">{r.products ? fmt(r.products) : <Dash />}</td>
              <td className="cc-num">{r.fixed_prices ? fmt(r.fixed_prices) : <Dash />}</td>
              {settings.map(f => (
                <td key={f.key} className={differ.has(f.key) ? 'is-diff' : undefined}>
                  <SettingValue field={f} value={r.settings[f.key]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Prices per restaurant ───────────────────────────────────────────────────

function PriceCell({ entry, product }) {
  if (!entry) return <td className="cc-num"><Dash /></td>;
  // What the restaurant effectively pays, as the server computed it (agreed
  // price if set, else the last price on a document — lib/ccSupplierStats).
  const price = entry.effective;
  if (price == null) return <td className="cc-num"><Dash /></td>;
  const spread = product.spread_pct > 0;
  const tone = spread && price === product.min ? ' is-min' : spread && price === product.max ? ' is-max' : '';
  const title = entry.fixed_price > 0 ? `מחיר מוסכם · מחיר אחרון במסמך ${formatCurrency(entry.last_price)}` : 'מחיר אחרון במסמך';
  return (
    <td className={`cc-num cc-price${tone}`} title={title}>
      {formatCurrency(price)}
      {entry.fixed_price > 0 && <span className="cc-price-tag">מוסכם</span>}
    </td>
  );
}

function usePriceRows(products, q, sharedOnly) {
  return useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter(p => (!sharedOnly || p.restaurants > 1)
      && (!needle || p.name.toLowerCase().includes(needle) || p.sku.toLowerCase().includes(needle)));
  }, [products, q, sharedOnly]);
}

function PricesTable({ detail }) {
  const [q, setQ] = useState('');
  const [sharedOnly, setSharedOnly] = useState(detail.shared_products > 0);
  const rows = usePriceRows(detail.products, q, sharedOnly);
  const truncated = detail.total_products > detail.products.length;

  return (
    <>
      <div className="cc-card-head" style={{ flexWrap: 'wrap' }}>
        <h3 className="cc-card-title">מחירים לפי מסעדה</h3>
        <span className="cc-muted" style={{ fontSize: '0.79rem' }}>
          {fmt(detail.shared_products)} מתוך {fmt(detail.total_products)} מוצרים נקנים ביותר ממסעדה אחת
          {truncated && ` · מוצגים ${fmt(detail.products.length)} הראשונים`}
        </span>
        <span className="cc-spacer" />
        <label className="cc-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={sharedOnly} onChange={e => setSharedOnly(e.target.checked)} />
          רק מוצרים של כמה מסעדות
        </label>
        <input className="form-control" style={{ maxWidth: 200 }} value={q}
          onChange={e => setQ(e.target.value)} placeholder="חיפוש מוצר או מק״ט…" aria-label="חיפוש מוצר" />
      </div>
      {rows.length === 0 ? (
        <div className="cc-empty">אין מוצרים להצגה</div>
      ) : (
        <div className="cc-matrix-scroll">
          <table className="cc-matrix">
            <thead>
              <tr>
                <th className="cc-matrix-name">מוצר</th>
                <th>מק״ט</th>
                {detail.restaurants.map(r => <th key={r.id}>{r.name}</th>)}
                <th title="הפער בין המחיר הגבוה לנמוך, באחוזים מהנמוך">פער</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => {
                const byRestaurant = new Map(p.prices.map(x => [x.restaurant_id, x]));
                return (
                  <tr key={p.key}>
                    <td className="cc-matrix-name">{p.name}</td>
                    <td className="cc-muted" style={{ direction: 'ltr' }}>{p.sku || '—'}</td>
                    {detail.restaurants.map(r => <PriceCell key={r.id} entry={byRestaurant.get(r.id)} product={p} />)}
                    <td className="cc-num" style={{ fontWeight: 700 }}>{p.spread_pct ? `${p.spread_pct}%` : <Dash />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── The modal ───────────────────────────────────────────────────────────────

function useSupplierDetail(supplierKey) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setError(false);
    setDetail(null);
    try {
      const r = await commandCenterApi.getSupplierDetail(supplierKey);
      setDetail(r.data);
    } catch {
      setError(true);
    }
  }, [supplierKey]);
  useEffect(() => { load(); }, [load]);
  return { detail, error, load };
}

export default function SupplierDetailModal({ supplierKey, name, settings, onClose }) {
  const { detail, error, load } = useSupplierDetail(supplierKey);
  return (
    <Modal onClose={onClose} size={1100} ariaLabel={`פרטי ספק: ${name}`}>
      <h2 className="modal-title" style={{ marginBottom: 4 }}>{name}</h2>
      <LoadState loading={!detail && !error} error={error} onRetry={load} empty={false}>
        {detail && (
          <>
            <p className="cc-muted" style={{ marginTop: 0, fontSize: '0.83rem' }}>
              {fmt(detail.restaurants.length)} מסעדות
              {detail.variants.length > 1 && ` · נכתב גם: ${detail.variants.filter(v => v !== name).join(' · ')}`}
            </p>
            <section className="cc-tabpanel-section" style={{ marginTop: 12 }}>
              <div className="cc-card-head">
                <h3 className="cc-card-title">הגדרות לפי מסעדה</h3>
                {detail.differs.length > 0 && (
                  <span className="cc-pill warn">{fmt(detail.differs.length)} הגדרות שונות בין המסעדות</span>
                )}
              </div>
              <SettingsTable rows={detail.rows} settings={settings} differs={detail.differs} />
            </section>
            <section className="cc-tabpanel-section">
              <PricesTable detail={detail} />
            </section>
          </>
        )}
      </LoadState>
    </Modal>
  );
}
